import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { formatSalary, timeAgo, MODALITY_LABELS, TYPE_LABELS, EXP_LABELS } from '../services/api';
import { useAuth } from '../context/AuthContext';
import JobCard from '../components/jobs/JobCard';
import styles from './JobDetail.module.css';
import { shareJob, shareOnLinkedIn } from '../services/sharing';
import PageSeo from '../components/seo/PageSeo';
import { COUNTRIES } from '../services/country';

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then(r => {
      setJob(r.data);
      setLoading(false);
    }).catch(() => { setError(true); setLoading(false); });

    if (user?.role === 'candidate') {
      api.get('/saved-jobs').then(r => setSaved(r.data.some(j=>j.id===Number(id))));
      api.get('/applications/my').then(r => setApplied(r.data.applications.some(a=>a.job_id===Number(id))));
    }
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) return navigate('/login');
    if (saved) { await api.delete(`/saved-jobs/${id}`); setSaved(false); }
    else { await api.post(`/saved-jobs/${id}`); setSaved(true); }
  };

  const handleApply = async () => {
    if (!user) return navigate('/login');
    if (user.role !== 'candidate') return;
    setApplying(true); setApplyMsg(null);
    try {
      await api.post('/applications', { job_id: Number(id), cover_letter: coverLetter });
      setApplied(true); setShowApply(false);
      setApplyMsg({ type:'success', text:'¡Postulación enviada! La empresa revisará tu perfil pronto.' });
    } catch (err) {
      setApplyMsg({ type:'error', text: err.response?.data?.error || 'Error al postular' });
    } finally { setApplying(false); }
  };

  if (loading) return <div className="page-loading"><div className="spinner"/><span>Cargando oferta...</span></div>;
  if (error) return <div className={styles.errorPage}><h2>Oferta no encontrada</h2><Link to="/jobs" className="btn btn-primary">Ver empleos</Link></div>;

  const skills = typeof job.skills_required === 'string' ? JSON.parse(job.skills_required || '[]') : (job.skills_required || []);
  const daysLeft = Math.ceil((new Date(job.expires_at) - Date.now()) / 86400000);
  const reportJob=async()=>{if(!user)return navigate('/login');const reason=window.prompt('¿Por qué deseas reportar esta oferta?');if(reason?.trim()){await api.post('/reports',{target_type:'job',target_id:job.id,reason});window.alert('Gracias. Revisaremos tu reporte.')}};
  const structuredData={
    '@context':'https://schema.org','@type':'JobPosting',title:job.title,description:job.description,
    datePosted:job.published_at,validThrough:job.expires_at,employmentType:job.job_type?.toUpperCase(),
    hiringOrganization:{'@type':'Organization',name:job.company_name,sameAs:job.website||undefined,logo:job.logo_url||undefined},
    jobLocation:job.modality==='remote'?undefined:{'@type':'Place',address:{'@type':'PostalAddress',addressLocality:job.city,addressRegion:job.province,addressCountry:job.country_code||'DO'}},
    jobLocationType:job.modality==='remote'?'TELECOMMUTE':undefined,
    applicantLocationRequirements:job.modality==='remote'?{'@type':'Country',name:job.country||'República Dominicana'}:undefined,
    baseSalary:job.salary_visible&&job.salary_min?{'@type':'MonetaryAmount',currency:job.salary_currency||'DOP',value:{'@type':'QuantitativeValue',minValue:Number(job.salary_min),maxValue:Number(job.salary_max||job.salary_min),unitText:job.salary_period==='annual'?'YEAR':'MONTH'}}:undefined,
  };
  const jobCountry=COUNTRIES[job.country_code]||COUNTRIES.DO;
  const jobCanonical=`https://talentosrd.com/${jobCountry.slug}/jobs/${job.id}`;
  const jobSeoTitle=job.country_code==='BR'?`${job.title} em ${job.company_name} | TalentosRD`:`${job.title} en ${job.company_name} | TalentosRD`;
  const jobSeoDescription=job.description?.replace(/\s+/g,' ').slice(0,155)||jobSeoTitle;

  return (
    <div className={`container ${styles.layout}`}>
      <PageSeo title={jobSeoTitle} description={jobSeoDescription} canonical={jobCanonical} noindex={Boolean(job.is_demo)} structuredData={job.is_demo?null:structuredData}/>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/">Inicio</Link> › <Link to="/jobs">Empleos</Link> › {job.category_name && <><Link to={`/jobs?category=${job.category_slug}`}>{job.category_name}</Link> › </>}{job.title}
      </div>

      <div className={styles.content}>
        {/* Main column */}
        <div className={styles.main}>
          {/* Header card */}
          <div className={`card ${styles.headerCard}`}>
            <div className={styles.headerTop}>
              <div className={styles.companyLogo}>
                {job.logo_url ? <img src={job.logo_url} alt={job.company_name}/> : <span>{job.company_name[0]}</span>}
              </div>
              <div className={styles.headerInfo}>
                <h1>{job.title}</h1>
                <div className={styles.companyLine}>
                  <Link to={`/companies/${job.company_slug}`} className={styles.companyName}>{job.company_name}</Link>
                  {job.company_verified && <span className="badge badge-blue">✓ Verificada</span>}
                  <span className={styles.dot}>·</span>
                  <span>{job.city}, {job.province}</span>
                </div>
                <div className={styles.headerBadges}>
                  <span className={`badge ${job.modality==='remote'?'badge-green':job.modality==='hybrid'?'badge-amber':'badge-blue'}`}>{MODALITY_LABELS[job.modality]}</span>
                  <span className="badge badge-gray">{TYPE_LABELS[job.job_type]}</span>
                  {job.is_featured && <span className="badge badge-amber">⭐ Destacado</span>}
                  {job.is_urgent && <span className="badge badge-red">🔥 Urgente</span>}
                  {job.is_demo && <span className="badge badge-amber">Vacante de demostración</span>}
                </div>
              </div>
            </div>

            <div className={styles.keyInfo}>
              <div className={styles.keyItem}><span className={styles.keyLabel}>Salario</span><strong>{formatSalary(job.salary_min,job.salary_max,job.salary_currency,job.salary_visible,job.salary_period)}</strong></div>
              <div className={styles.keyItem}><span className={styles.keyLabel}>Experiencia</span><strong>{EXP_LABELS[job.experience_level]}</strong></div>
              <div className={styles.keyItem}><span className={styles.keyLabel}>Vacantes</span><strong>{job.vacancies} puesto{job.vacancies>1?'s':''}</strong></div>
              <div className={styles.keyItem}><span className={styles.keyLabel}>Publicado</span><strong>{timeAgo(job.published_at)}</strong></div>
              {daysLeft > 0 && <div className={styles.keyItem}><span className={styles.keyLabel}>Expira en</span><strong className={daysLeft<7?styles.expiringSoon:''}>{daysLeft} día{daysLeft!==1?'s':''}</strong></div>}
              <div className={styles.keyItem}><span className={styles.keyLabel}>Vistas</span><strong>{(job.views||0).toLocaleString()}</strong></div>
            </div>

            {applyMsg && <div className={`alert alert-${applyMsg.type} ${styles.applyMsg}`}>{applyMsg.text}</div>}

            <div className={styles.actions}>
              {job.is_demo ? <div className="alert alert-warning">Esta vacante sirve para probar la plataforma y no acepta postulaciones.</div> : !applied ? (
                <>
                  {!showApply ? (
                    <button onClick={()=>user?setShowApply(true):navigate('/login')} className="btn btn-primary btn-lg">Postularme ahora</button>
                  ) : (
                    <div className={styles.applyBox}>
                      <h4>Carta de presentación (opcional)</h4>
                      <textarea value={coverLetter} onChange={e=>setCoverLetter(e.target.value)} placeholder="Cuéntale a la empresa por qué eres el candidato ideal..." rows={5} style={{width:'100%',padding:'0.75rem',border:'1.5px solid var(--gray-300)',borderRadius:'var(--border-radius)',fontSize:'0.9rem'}}/>
                      <div className={styles.applyBtns}>
                        <button onClick={handleApply} disabled={applying} className="btn btn-primary">{applying?'Enviando...':'Enviar postulación'}</button>
                        <button onClick={()=>setShowApply(false)} className="btn btn-ghost">Cancelar</button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-success">✓ Ya te has postulado a este empleo</div>
              )}
              <button onClick={toggleSave} className={`btn btn-ghost ${saved?styles.savedBtn:''}`}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill={saved?'currentColor':'none'} stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
                {saved ? 'Guardado' : 'Guardar'}
              </button>
              <button onClick={()=>shareJob(job).catch(()=>{})} className="btn btn-ghost">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/></svg>
                Compartir
              </button>
              <button onClick={()=>shareOnLinkedIn(job)} className={`btn ${styles.linkedinBtn}`} aria-label="Compartir vacante en LinkedIn">
                <span className={styles.linkedinIcon}>in</span> LinkedIn
              </button>
              {user&&<button onClick={reportJob} className="btn btn-ghost">Reportar</button>}
            </div>
          </div>

          {/* Description sections */}
          {[
            {title:'Descripción del puesto', content: job.description},
            {title:'Responsabilidades', content: job.responsibilities},
            {title:'Requisitos', content: job.requirements},
            {title:'Beneficios', content: job.benefits},
          ].filter(s=>s.content).map(s=>(
            <div key={s.title} className={`card ${styles.section}`}>
              <h2>{s.title}</h2>
              <div className={styles.richText}>
                {s.content.split('\n').filter(Boolean).map((line,i)=>(
                  <p key={i}>{line.startsWith('-')||line.startsWith('•') ? <><span className={styles.bullet}>▸</span>{line.slice(1).trim()}</> : line}</p>
                ))}
              </div>
            </div>
          ))}

          {skills.length > 0 && (
            <div className={`card ${styles.section}`}>
              <h2>Habilidades requeridas</h2>
              <div className={styles.skillsTags}>{skills.map((s,i)=><span key={i} className="tag">{s}</span>)}</div>
            </div>
          )}

          {/* Related jobs */}
          {job.related?.length > 0 && (
            <div className={styles.related}>
              <h2>Empleos similares</h2>
              <div className={styles.relatedGrid}>{job.related.map(j=><JobCard key={j.id} job={j} compact/>)}</div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className={styles.sidebar}>
          <div className={`card ${styles.companyCard}`}>
            <div className={styles.companyCardTop}>
              <div className={styles.companyCardLogo}>
                {job.logo_url ? <img src={job.logo_url} alt={job.company_name}/> : <span>{job.company_name[0]}</span>}
              </div>
              <div>
                <Link to={`/companies/${job.company_slug}`} className={styles.companyCardName}>{job.company_name}</Link>
                {job.company_verified && <div><span className="badge badge-blue" style={{fontSize:'0.7rem'}}>✓ Verificada</span></div>}
              </div>
            </div>
            {job.company_desc && <p className={styles.companyCardDesc}>{job.company_desc.slice(0,200)}...</p>}
            <div className={styles.companyMeta}>
              {job.industry && <div><span>🏢</span> {job.industry}</div>}
              {job.size && <div><span>👥</span> {job.size} empleados</div>}
              {job.company_city && <div><span>📍</span> {job.company_city}</div>}
              {job.website && <div><span>🌐</span> <a href={job.website} target="_blank" rel="noopener noreferrer">{job.website.replace('https://','')}</a></div>}
              <div><span>💼</span> {job.company_active_jobs} ofertas activas</div>
            </div>
            <Link to={`/companies/${job.company_slug}`} className={`btn btn-outline ${styles.viewCoBtn}`}>Ver empresa completa</Link>
          </div>

          <div className={`card ${styles.infoCard}`}>
            <h3>Información del puesto</h3>
            <div className={styles.infoItems}>
              <div><span>📋</span> <div><label>Tipo</label><strong>{TYPE_LABELS[job.job_type]}</strong></div></div>
              <div><span>🌐</span> <div><label>Modalidad</label><strong>{MODALITY_LABELS[job.modality]}</strong></div></div>
              <div><span>📊</span> <div><label>Nivel</label><strong>{EXP_LABELS[job.experience_level]}</strong></div></div>
              {job.education_level && <div><span>🎓</span> <div><label>Educación</label><strong>{job.education_level}</strong></div></div>}
              <div><span>🏙️</span> <div><label>Ubicación</label><strong>{job.city}, {job.province}</strong></div></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
